/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import Pagination from "rc-pagination";

import {
  DetailsList,
  DetailsListLayoutMode,
  Dropdown,
  IColumn,
  SelectionMode,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";

import * as BenefitConstants from "Constants/BenefitConstants";

import ChevronIcon from "Components/Common/ChevronIcon/ChevronIcon";
import { PaginationRow } from "Components/Common/Pagination/Pagination.styles";
import { detailListStyles, spinnerStyles } from "Components/Common/SharedListStyles.styles";
import StatusElement from "Components/Common/Status/StatusElement";
import { IBenefit } from "Interfaces/Benefits/IBenefit";
import { formatDate } from "Utility/FormatHelper";
import { IBenefitsListProps } from "./IBenefitsListProps";

const BenefitsList: React.FunctionComponent<IBenefitsListProps> = (props) => {
  const {
    data,
    actions,
    loadBenefitsError,
    isLoadingBenefits,
    filteredCount,
    currentPage,
    pageSize,
    hasSwagKitCol,
    hasCountryCodePoolCol,
    hasSkuCol,
    benefitName,
  } = props;

  const formatDisplayDate = (incomingDate: Date | undefined): React.ReactElement => {
    if (incomingDate) {
      return <span>{formatDate(incomingDate)}</span>;
    } else {
      return <></>;
    }
  };

  const formatStatusColumn = (statusKey: string): React.ReactElement => {
    return <StatusElement statusValue={statusKey} />;
  };

  /**
   * Handle display of country-code based on EEA/ROW
   * @param poolType type of code
   * @returns
   */
  const formatCountryCodeColumn = (poolType): React.ReactElement => {
    if (poolType) {
      let labelText = "";
      if (benefitName == BenefitConstants.BenefitNameEnum.O365) {
        const getLabel = BenefitConstants.o365CountryCodePool.find((x) =>
          x.values?.includes(poolType)
        );
        labelText = getLabel && getLabel.text ? getLabel.text : "";
      } else if (benefitName == BenefitConstants.BenefitNameEnum.MS365) {
        const getLabel = BenefitConstants.ms365CountryCodePool.find((x) => x.key == poolType);
        labelText = getLabel && getLabel.text ? getLabel.text : "";
      } else {
        const getLabel = BenefitConstants.teamsCountryCodePool.find((x) => x.key == poolType);
        labelText = getLabel && getLabel.text ? getLabel.text : "";
      }
      return <span>{labelText}</span>;
    } else {
      return <></>;
    }
  };

  const columns: IColumn[] = [
    {
      key: "batchId",
      name: "Batch ID",
      ariaLabel: "Batch ID",
      fieldName: "batchId",
      minWidth: 80,
      isResizable: true,
    },
    {
      key: "key",
      name: "Code",
      ariaLabel: "Code",
      fieldName: "key",
      minWidth: 100,
      isResizable: true,
      isSorted: false,
    },
    {
      key: "status",
      name: "Status",
      ariaLabel: "Status",
      fieldName: "status",
      minWidth: 100,
      onRender: (item: IBenefit): React.ReactElement => {
        return formatStatusColumn(item.status);
      },
      isResizable: true,
    },
    {
      key: "uploadDate",
      name: "Code Uploaded Date",
      ariaLabel: "Date code was uploaded",
      fieldName: "uploadDate",
      minWidth: 160,
      onRender: (item: IBenefit): React.ReactElement => {
        return formatDisplayDate(item.dateUploaded);
      },
      isResizable: true,
    },
    {
      key: "expirationDate",
      name: "Code Expiration Date",
      ariaLabel: "Date code expires",
      fieldName: "expirationDate",
      minWidth: 160,
      onRender: (item: IBenefit): React.ReactElement => {
        return formatDisplayDate(item.keyExpirationDate);
      },
    },
    {
      key: "claimedDate",
      name: "Code Claimed Date",
      ariaLabel: "Date code was claimed",
      fieldName: "claimedDate",
      minWidth: 160,
      onRender: (item: IBenefit): React.ReactElement => {
        return formatDisplayDate(item.dateClaimed);
      },
      isResizable: true,
    },
    {
      key: "awardPeriodName",
      name: "Award Period",
      ariaLabel: "Name of Award Period code was claimed during",
      fieldName: "awardPeriodName",
      minWidth: 160,
      isResizable: true,
    },
    {
      key: "userProfileId",
      name: "User ID",
      ariaLabel: "User ID of the assigned user",
      fieldName: "userProfileId",
      minWidth: 80,
      isResizable: true,
    },
    {
      key: "assignedTo",
      name: "Assigned to (User Email)",
      ariaLabel: "Assigned to (User Email)",
      fieldName: "assignedTo",
      minWidth: 160,
      isResizable: true,
    },
    {
      key: "claimedBy",
      name: "Claimed by (User Email)",
      ariaLabel: "Claimed by (User Email)",
      fieldName: "claimedBy",
      minWidth: 160,
      isResizable: true,
    },
  ];

  /**
   * Add field in table-column for Benefits : Teams and O365
   */
  if (hasCountryCodePoolCol) {
    const copyObject = Object.assign(BenefitConstants.CountryCodePoolColumn);
    (copyObject.onRender = (item: IBenefit): React.ReactElement => {
      return formatCountryCodeColumn(item.countryCodePoolName);
    }),
      columns.splice(1, 0, copyObject);
  }

  /**
   * Add field in table-column for Benefits : MVP Award Kit
   */
  if (hasSkuCol) {
    const copyObject = Object.assign(BenefitConstants.SKUColumn);
    columns.splice(1, 0, copyObject);
  }

  /**
   * Add field in table-column for Benefits : Swag Kit
   */
  if (hasSwagKitCol) {
    const copyObject = Object.assign(BenefitConstants.SwagKitColumn);
    columns.splice(1, 0, copyObject);
  }

  if (isLoadingBenefits) {
    return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
  } else if (loadBenefitsError !== "") {
    return <div>{loadBenefitsError}</div>;
  }

  return (
    <>
      <DetailsList
        items={data}
        columns={columns}
        selectionMode={SelectionMode.none}
        getKey={(item: IBenefit): string => `${item.batchId}${item.key}`}
        setKey="none"
        layoutMode={DetailsListLayoutMode.fixedColumns}
        isHeaderVisible={true}
        //onRenderRow={onRenderRow}
        //onRenderItemColumn={onRenderColumn}
        enterModalSelectionOnTouch={true}
        styles={detailListStyles}
      />
      <PaginationRow>
        <Pagination
          total={filteredCount}
          onChange={(page) => actions.updateCurrentPage(page)}
          showTitle={false}
          current={currentPage}
          prevIcon={ChevronIcon("ChevronLeft")}
          nextIcon={ChevronIcon("ChevronRight")}
          pageSize={pageSize}
          showSizeChanger
        />
        <Dropdown
          options={[
            { key: 16, text: "Show 16 results" },
            { key: 32, text: "Show 32 results" },
            { key: 48, text: "Show 48 results" },
          ]}
          onChange={(_e, op): void => {
            actions.updatePageSize(op?.key as number);
          }}
          selectedKey={pageSize}
          className="pagination-dropdown"
          title="Page Size Dropdown"
        />
      </PaginationRow>
    </>
  );
};

export default BenefitsList;
